import React, { useCallback, useMemo, useRef, useState } from 'react';
import SafeHtml from '../../components/SafeHtml';
import { prepareCustomHtmlBlock } from '../../utils/customCss';
import {
  CITATION_OVERLAY_CLASS,
  CITATION_OVERLAY_GRIP_CLASS,
  citationOverlayText,
  normalizeCitationOverlayPos,
  wantsCitationOverlay
} from '../../utils/customHtmlCitationOverlay';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * The Custom HTML/CSS block: an author-supplied layout that takes over the body
 * of one slide.
 *
 * Unlike the embedded-HTML block this renders in the app's own DOM rather than
 * a sandboxed iframe, because its whole purpose is CSS that reaches the slide —
 * an iframe would seal the rules inside it and the "apply to the whole deck"
 * option could not exist at all. The markup is sanitized with the slide-layout
 * profile and the CSS is prefixed with this block's own selector, so a slide's
 * rules stay on that slide unless the author ticks deck scope.
 *
 * Deck-scoped CSS is emitted by LessonTemplate instead (one document-level
 * style element built from every deck-scoped block), so the rules stay live
 * while a different slide is on screen.
 */
const CustomHtmlRenderer = ({ block, isEditMode, canMoveCitation, onBlockUpdate }) => {
  // On a canvas slide the block renders read-only, but the citation note is not
  // one of the designer's objects — it belongs to this block — so the block is
  // the only thing that can offer to move it.
  const citationDraggable = (canMoveCitation ?? isEditMode) && typeof onBlockUpdate === 'function';

  // The note is a card of this component's own here, so the shared preparation
  // is asked to leave it out of the markup. It still goes into the markup for
  // the exported page and the rasterized slide, which have no React to draw it
  // with. See prepareCustomHtmlBlock.
  const { html, css, imageVarsCss, resetCss, baseCss, deckScoped } = useMemo(
    () => prepareCustomHtmlBlock(block, { inlineCitationNote: false }),
    [block]
  );

  const noteText = useMemo(
    () => (wantsCitationOverlay(block) ? citationOverlayText(block) : ''),
    [block]
  );

  const hostRef = useRef(null);
  const [dragPos, setDragPos] = useState(null);
  const dragRef = useRef(null);

  /**
   * Dragging the credit line.
   *
   * It is its own element, laid over the layout rather than written into it,
   * for one practical reason: on a canvas slide the designer covers every block
   * with a full-size layer that turns any drag into "move this block". A note
   * inside the block's markup sits under that layer and can never be picked up.
   * This card sits above it and takes the gesture itself.
   *
   * What is saved is a percentage of the block's own box, so it holds at
   * presentation scale and in the exported file, where the same slide is a
   * different number of pixels wide.
   */
  const onPointerDown = useCallback((event) => {
    if (!citationDraggable) return;
    if (event.button != null && event.button !== 0) return;
    const host = hostRef.current;
    const note = event.currentTarget;
    if (!host) return;
    const hostRect = host.getBoundingClientRect();
    const noteRect = note.getBoundingClientRect();
    if (!hostRect.width || !hostRect.height) return;
    // The designer is listening for this on the layer underneath; taking it
    // here is what keeps a drag of the note from becoming a drag of the slide.
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      hostRect,
      noteW: noteRect.width,
      noteH: noteRect.height,
      grabX: event.clientX - noteRect.left,
      grabY: event.clientY - noteRect.top,
      pos: null
    };
    if (note.setPointerCapture) note.setPointerCapture(event.pointerId);
  }, [citationDraggable]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag) return;
    event.preventDefault();
    event.stopPropagation();
    const { hostRect } = drag;
    const x = clamp(event.clientX - hostRect.left - drag.grabX, 0, Math.max(0, hostRect.width - drag.noteW));
    const y = clamp(event.clientY - hostRect.top - drag.grabY, 0, Math.max(0, hostRect.height - drag.noteH));
    drag.pos = {
      x: (x / hostRect.width) * 100,
      y: (y / hostRect.height) * 100
    };
    setDragPos(drag.pos);
  }, []);

  const onPointerUp = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    const note = event.currentTarget;
    if (note.releasePointerCapture && note.hasPointerCapture && note.hasPointerCapture(event.pointerId)) {
      note.releasePointerCapture(event.pointerId);
    }
    event.stopPropagation();
    setDragPos(null);
    if (!drag.pos) return;
    onBlockUpdate?.({ ...block, citationOverlayPos: normalizeCitationOverlayPos(drag.pos) });
  }, [block, onBlockUpdate]);

  // Uploads stay scoped to this block even when the author's own rules are
  // global, so a deck-scoped block still gets its own pictures.
  //
  // The CSS reset ships here too, even though the app page already runs
  // Tailwind's preflight. Preflight's `h2 { font-size: inherit }` is a bare
  // element selector, so inside a canvas frame it loses to
  // `.slide-designer__item h2`, which sizes headings as a share of the slide.
  // The exported file has no such rule and used this block-scoped reset
  // instead — so the same unstyled heading came out slide-scaled in the editor
  // and inherited in the export. Emitting the identical sheet in both is what
  // makes the two agree by construction rather than by coincidence.
  const localCss = [resetCss, baseCss, imageVarsCss, deckScoped ? '' : css].filter(Boolean).join('\n\n');

  const isEmpty = !html.trim() && !css.trim() && !imageVarsCss;

  if (isEmpty) {
    return (
      <div className="slide-custom-html slide-custom-html--empty" data-block={block.id}>
        <div className="text-4xl" aria-hidden="true">🧩</div>
        <h3 className="text-base font-semibold text-gray-700 mt-2">Custom HTML / CSS</h3>
        <p className="text-sm text-gray-500 mt-1">
          {isEditMode
            ? 'Click the edit button on this block to write the markup and styles for this slide.'
            : 'No custom layout has been written for this slide yet.'}
        </p>
      </div>
    );
  }

  // While a drag is in flight the card follows the pointer from state; the rest
  // of the time the stylesheet places it, so a position cleared in the editor
  // really is cleared.
  const dragStyle = dragPos
    ? { left: `${dragPos.x}%`, top: `${dragPos.y}%`, right: 'auto', bottom: 'auto' }
    : undefined;

  return (
    <>
      {/* Slide-scoped rules ride with the block. Deck-scoped ones are hoisted
          to the document so they survive navigating away from this slide. */}
      {localCss && <style>{localCss}</style>}
      <div
        ref={hostRef}
        className={`slide-custom-html${citationDraggable ? ' slide-custom-html--note-movable' : ''}`}
        data-block={block.id}
        data-css-scope={deckScoped ? 'deck' : 'slide'}
      >
        {/* `display: contents` — the layout's own boxes stay direct children of
            the block for layout, so percentage heights, cqw units and the
            author's own selectors resolve exactly as they did when the markup
            was the block's only content. */}
        <SafeHtml
          profile="slideLayout"
          className="slide-custom-html__layout"
          html={html}
        />
        {noteText && (
          <div
            className={`${CITATION_OVERLAY_CLASS}${citationDraggable ? ' is-movable' : ''}`}
            style={dragStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={citationDraggable ? (event) => event.stopPropagation() : undefined}
            onDragStart={citationDraggable ? (event) => {
              event.preventDefault();
              event.stopPropagation();
            } : undefined}
            draggable={false}
            title={citationDraggable ? 'Drag to move this note' : undefined}
          >
            {/* Editor-only, and drawn here rather than in the shared markup so
                the exported page still gets the credit line on its own. */}
            {citationDraggable && (
              <span className={CITATION_OVERLAY_GRIP_CLASS} aria-hidden="true" />
            )}
            {noteText}
          </div>
        )}
      </div>
      {isEditMode && deckScoped && (
        <p className="slide-custom-html__scope-note">
          This block's CSS is applied to every slide in the deck.
        </p>
      )}
    </>
  );
};

export default CustomHtmlRenderer;
